"use client";

import React from "react";

import {
  BadgeCheck,
  Moon,
  Sun,
  LogOut,
  Sparkles,
  LaptopMinimal,
  SunMoon,
  SquareStack,
  BookOpen,
  Layers,
  Check,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "next-themes";
import { signOut } from "@/lib/auth-client";
import { Button } from "./ui/button";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { updatePreferredReadingMode } from "@/app/actions/user/updatePreferredReadingMode";
import { toast } from "sonner";
import { sanitizeImageUrl } from "@/lib/image-safety";

export function UserDropdown({
  user,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
    subscriptionStatus: string;
    daysLeftInSubscription: number;
    preferredReadingMode?: "story" | "summary";
  };
}) {
  const { setTheme } = useTheme();
  const router = useRouter();
  const [preferredReadingMode, setPreferredReadingMode] = React.useState<
    "story" | "summary"
  >(user.preferredReadingMode ?? "story");
  const safeAvatar = sanitizeImageUrl(user.avatar) || undefined;

  const handleReadingModeChange = async (mode: "story" | "summary") => {
    if (mode === preferredReadingMode) return;

    const previousMode = preferredReadingMode;
    setPreferredReadingMode(mode);

    const result = await updatePreferredReadingMode(mode);
    if (!result.success) {
      setPreferredReadingMode(previousMode);
      toast.error(result.error || "Could not update default reading mode");
      return;
    }

    router.refresh();
  };

  const handleLogOut = async () => {
    await signOut();
    router.refresh();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="block" asChild>
        <Button
          size="icon"
          className="size-10 lg:size-11 aspect-square inline-flex items-center justify-center rounded-full p-0"
          variant={"secondary"}
        >
          <Avatar className="size-10 lg:size-11 rounded-full">
            <AvatarImage src={safeAvatar} alt={user.name} />
            <AvatarFallback className="rounded-lg">
              {user?.name ? user?.name[0] : "B"}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        side="bottom"
        align="end"
        sideOffset={8}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-full">
              <AvatarImage src={safeAvatar} alt={user.name} />
              <AvatarFallback className="rounded-lg">
                {user?.name ? user?.name[0] : "B"}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold inline-flex items-center gap-1">
                {user.name}
                {user.subscriptionStatus === "active" && (
                  <BadgeCheck className="inline-block size-4 text-primary drop-shadow-cyan-500 drop-shadow-lg" />
                )}
              </span>
              <span className="truncate text-xs">{user.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link href="/plan">
              <Sparkles />
              {user.subscriptionStatus === "active"
                ? `Pro Plan (${user.daysLeftInSubscription} days left)`
                : "Upgrade to Pro"}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {/* <DropdownMenuItem>
            <BadgeCheck />
            Account
          </DropdownMenuItem> */}
          <DropdownMenuItem asChild>
            <Link href="/me">
              <SquareStack />
              My Bites
            </Link>
          </DropdownMenuItem>
          {/* <DropdownMenuItem>
            <CreditCard />
            Billing
          </DropdownMenuItem> */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <SunMoon />
              <span>Theme</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme("light")}>
                  <Sun />
                  <span>Light</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>
                  <Moon />
                  <span>Dark</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>
                  <LaptopMinimal />
                  <span>System</span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <BookOpen />
              <span>Reading Mode</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  onClick={() => handleReadingModeChange("story")}
                >
                  <BookOpen />
                  <span>Story</span>
                  {preferredReadingMode === "story" && (
                    <Check className="ml-auto" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleReadingModeChange("summary")}
                >
                  <Layers />
                  <span>Summary</span>
                  {preferredReadingMode === "summary" && (
                    <Check className="ml-auto" />
                  )}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogOut}>
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
